import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ChevronRight, Upload, Download } from 'lucide-react';
import { customerApi, companyApi, type Customer } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useConfirm } from '../ConfirmProvider';
import { useToast } from '../ToastProvider';
import { Button, Card, EmptyState, ErrorNote, Field, PageHeader, displayCustomer } from '../ui';

const empty: Partial<Customer> = { country: 'Deutschland', paymentTerms: 14, kind: 'customer' };

// Gehoert ein Kontakt zum Tab? 'both' zaehlt zu beiden.
function matchesTab(c: Customer, tab: 'customer' | 'supplier'): boolean {
  const k = c.kind ?? 'customer';
  return k === 'both' || k === tab;
}

// Tags aus CSV-String -> getrimmtes Array (leere weg).
function parseTags(s: string | null | undefined): string[] {
  return (s ?? '').split(',').map((t) => t.trim()).filter(Boolean);
}

// Spalten-Aliase fuer den CSV-Import (deutsch + englisch, tolerant).
const CSV_ALIASES: Record<string, keyof Customer> = {
  kundennummer: 'customerNumber', customernumber: 'customerNumber', nr: 'customerNumber',
  firma: 'companyName', companyname: 'companyName', unternehmen: 'companyName',
  vorname: 'firstName', firstname: 'firstName',
  nachname: 'lastName', lastname: 'lastName', name: 'lastName',
  email: 'email', 'e-mail': 'email', mail: 'email',
  telefon: 'phone', phone: 'phone', tel: 'phone',
  strasse: 'street', straße: 'street', street: 'street',
  plz: 'postalCode', postalcode: 'postalCode', postleitzahl: 'postalCode',
  ort: 'city', city: 'city', stadt: 'city',
  land: 'country', country: 'country',
  ustid: 'vatId', 'ust-idnr': 'vatId', vatid: 'vatId', ustidnr: 'vatId',
  ansprechpartner: 'contactPerson', contactperson: 'contactPerson',
  kategorie: 'category', category: 'category',
  branche: 'industry', industry: 'industry',
  region: 'region',
  tags: 'tags', schlagworte: 'tags',
  notizen: 'notes', notes: 'notes', notiz: 'notes',
};

// Minimaler CSV-Parser: erkennt ; oder , als Trenner, beachtet Quotes.
function parseCsv(text: string): Partial<Customer>[] {
  const clean = text.replace(/^﻿/, '');
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  const sep = (clean.split('\n')[0]?.split(';').length ?? 1) > (clean.split('\n')[0]?.split(',').length ?? 1) ? ';' : ',';
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    if (inQuotes) {
      if (ch === '"' && clean[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') inQuotes = false;
      else field += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === sep) { row.push(field); field = ''; }
    else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else if (ch === '\r') { /* skip */ }
    else field += ch;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  if (rows.length < 2) return [];

  const header = (rows[0] ?? []).map((h) => h.trim().toLowerCase());
  const out: Partial<Customer>[] = [];
  for (const r of rows.slice(1)) {
    if (r.every((c) => !c.trim())) continue;
    const obj: Partial<Customer> = {};
    header.forEach((h, idx) => {
      const key = CSV_ALIASES[h];
      const val = (r[idx] ?? '').trim();
      if (key && val) (obj as Record<string, unknown>)[key] = val;
    });
    out.push(obj);
  }
  return out;
}

export default function Customers(): ReactElement {
  const { active } = useCompany();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<Customer> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [touched, setTouched] = useState(false);

  // Aktiver Tab: Kunden oder Lieferanten.
  const [tab, setTab] = useState<'customer' | 'supplier'>('customer');

  // Filter-/Suchzustand.
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [vipOnly, setVipOnly] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // CSV-Import-Vorschau.
  const [importRows, setImportRows] = useState<Partial<Customer>[] | null>(null);
  const [importing, setImporting] = useState(false);

  // Erweiterte Kundenverwaltung (Detailseite, Kategorisierung/VIP/Tags) ab Plus.
  const [canCategorize, setCanCategorize] = useState(false);
  useEffect(() => {
    if (!active) return;
    companyApi.usage(active.id)
      .then((u) => setCanCategorize(u.features.includes('kundenverwaltung')))
      .catch(() => undefined);
  }, [active]);

  async function load() {
    if (!active) return;
    setLoading(true);
    try {
      setCustomers(await customerApi.list(active.id, { includeInactive: true }));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const nameMissing = !!editing && !(editing.companyName?.trim() || editing.firstName?.trim() || editing.lastName?.trim());
  const emailInvalid = !!editing?.email?.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editing.email.trim());

  // Vorhandene Kategorien fuer das Filter-Dropdown.
  const categories = useMemo(
    () => Array.from(new Set(customers.map((c) => c.category?.trim()).filter(Boolean))).sort() as string[],
    [customers],
  );

  // Anzahl je Tab (fuer die Badges).
  const counts = useMemo(() => ({
    customer: customers.filter((c) => c.active !== false && matchesTab(c, 'customer')).length,
    supplier: customers.filter((c) => c.active !== false && matchesTab(c, 'supplier')).length,
  }), [customers]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers.filter((c) => {
      if (!matchesTab(c, tab)) return false;
      if (!showInactive && c.active === false) return false;
      if (vipOnly && !c.vip) return false;
      if (catFilter && c.category !== catFilter) return false;
      if (q) {
        const hay = [
          displayCustomer(c), c.customerNumber, c.email, c.city, c.industry, c.region, c.tags,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [customers, tab, search, catFilter, vipOnly, showInactive]);

  function openEditor(c: Partial<Customer>) {
    setError(null);
    setTouched(false);
    setEditing(c);
  }

  async function save() {
    if (!active || !editing) return;
    if (nameMissing || emailInvalid) { setTouched(true); return; }
    setError(null);
    setBusy(true);
    try {
      if (editing.id) await customerApi.update(active.id, editing.id, editing);
      else await customerApi.create(active.id, editing);
      toast.success(editing.id ? 'Kunde gespeichert.' : 'Kunde angelegt.');
      setEditing(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!active) return;
    const ok = await confirm({ title: 'Kunde archivieren?', message: 'Der Kunde wird ausgeblendet. Bestehende Rechnungen bleiben erhalten.', confirmLabel: 'Archivieren' });
    if (!ok) return;
    await customerApi.remove(active.id, id);
    toast.info('Kunde archiviert.');
    await load();
  }

  async function dsgvoExport(id: string) {
    if (!active) return;
    try {
      const blob = await customerApi.dsgvoExport(active.id, id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `dsgvo-${id}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export fehlgeschlagen.');
    }
  }

  async function purge(id: string) {
    if (!active) return;
    const ok = await confirm({
      title: 'Kontakt endgültig löschen?',
      message: 'Stammdaten werden gelöscht. Hängen Rechnungen/Belege dran, wird der Kontakt aus GoBD-Gründen stattdessen anonymisiert (Buchungen bleiben erhalten).',
      confirmLabel: 'Endgültig löschen',
    });
    if (!ok) return;
    try {
      const res = await customerApi.purge(active.id, id);
      toast.success(res.mode === 'deleted' ? 'Kontakt gelöscht.' : 'Kontakt anonymisiert (Buchungen bleiben).');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    }
  }

  async function handleExport() {
    if (!active) return;
    try {
      const blob = await customerApi.exportCsv(active.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kunden.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export fehlgeschlagen.');
    }
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // gleiche Datei erneut waehlbar
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(String(reader.result ?? ''));
      if (rows.length === 0) { toast.error('Keine Zeilen erkannt. Erwartet wird eine CSV mit Kopfzeile.'); return; }
      setImportRows(rows);
    };
    reader.readAsText(file, 'utf-8');
  }

  async function confirmImport() {
    if (!active || !importRows) return;
    setImporting(true);
    try {
      // Importierte Zeilen bekommen den Typ des aktiven Tabs.
      const rows = importRows.map((r) => ({ ...r, kind: r.kind ?? tab }));
      const res = await customerApi.importRows(active.id, rows);
      toast.success(`${res.created} Kontakte importiert${res.skipped ? `, ${res.skipped} übersprungen` : ''}.`);
      if (res.errors.length) res.errors.slice(0, 3).forEach((m) => toast.info(m));
      setImportRows(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Import fehlgeschlagen.');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Kontakte"
        subtitle="Kunden und Lieferanten zentral verwalten – getrennt nach Typ."
        action={(
          <div className="flex flex-wrap gap-2">
            {canCategorize && (
              <>
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300">
                  <Upload className="h-4 w-4" /> Import
                  <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
                </label>
                <Button variant="secondary" onClick={handleExport}><span className="inline-flex items-center gap-1.5"><Download className="h-4 w-4" /> Export</span></Button>
              </>
            )}
            <Button onClick={() => openEditor({ ...empty, kind: tab })}>
              + {tab === 'supplier' ? 'Neuer Lieferant' : 'Neuer Kunde'}
            </Button>
          </div>
        )}
      />

      {/* Tabs: Kunden / Lieferanten */}
      <div className="mb-4 flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {([['customer', 'Kunden'], ['supplier', 'Lieferanten']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setTab(key); setCatFilter(''); setVipOnly(false); }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              tab === key
                ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            {label} <span className="ml-1 text-xs text-gray-400">{counts[key]}</span>
          </button>
        ))}
      </div>

      {/* Such- und Filterleiste */}
      {customers.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen (Name, Nr., Ort, Tag …)"
            className="min-w-[200px] flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
          {canCategorize && categories.length > 0 && (
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">Alle Kategorien</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {canCategorize && (
            <button
              onClick={() => setVipOnly((v) => !v)}
              className={`inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                vipOnly ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300'
              }`}
            >
              <Star className={`h-4 w-4 ${vipOnly ? 'fill-amber-400 text-amber-400' : ''}`} /> VIP
            </button>
          )}
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="h-4 w-4" />
            Inaktive
          </label>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Lädt …</p>
      ) : visible.length === 0 && !search && !catFilter && !vipOnly ? (
        <EmptyState
          title={tab === 'supplier' ? 'Noch keine Lieferanten' : 'Noch keine Kunden'}
          hint={tab === 'supplier' ? 'Lege deinen ersten Lieferanten an oder ordne Belege zu.' : 'Lege deinen ersten Kunden an, um Rechnungen zu schreiben.'}
          action={<Button onClick={() => openEditor({ ...empty, kind: tab })}>+ {tab === 'supplier' ? 'Neuer Lieferant' : 'Neuer Kunde'}</Button>}
        />
      ) : visible.length === 0 ? (
        <Card className="text-center text-sm text-gray-500">Keine Kontakte für diese Filter.</Card>
      ) : (
        <div className="space-y-2">
          {visible.map((c) => (
            <Card key={c.id} className={`flex items-center justify-between !p-4 ${c.active === false ? 'opacity-60' : ''}`}>
              <button
                onClick={() => { if (canCategorize) navigate(`/app/kunden/${c.id}`); }}
                disabled={!canCategorize}
                className={`group flex min-w-0 flex-1 items-center gap-2 text-left ${canCategorize ? '' : 'cursor-default'}`}
              >
                {canCategorize && <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 transition group-hover:text-blue-500" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium text-gray-900 group-hover:text-blue-700 dark:text-gray-100">
                    {c.vip && <Star className="h-4 w-4 shrink-0 fill-amber-400 text-amber-400" />}
                    <span className="truncate">{displayCustomer(c)}</span>
                    {c.customerNumber && (
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{c.customerNumber}</span>
                    )}
                    {c.active === false && (
                      <span className="shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500">inaktiv</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
                    <span>{[c.email, c.city].filter(Boolean).join(' · ') || 'Keine weiteren Angaben'}</span>
                    {c.category && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">{c.category}</span>}
                    {parseTags(c.tags).map((t) => (
                      <span key={t} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">#{t}</span>
                    ))}
                  </div>
                </div>
              </button>
              <div className="flex shrink-0 gap-2">
                <Button variant="secondary" onClick={() => openEditor(c)}>
                  Bearbeiten
                </Button>
                {c.active !== false && (
                  <Button variant="ghost" onClick={() => remove(c.id)}>
                    Archivieren
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6">
            <h2 className="mb-4 text-lg font-bold text-gray-900">
              {editing.id ? 'Kontakt bearbeiten' : ((editing.kind ?? 'customer') === 'supplier' ? 'Neuer Lieferant' : 'Neuer Kunde')}
            </h2>
            <p className="-mt-2 mb-4 text-sm text-gray-500">Firma <em>oder</em> Vor-/Nachname genügt – die Adresse erscheint später auf Rechnungen.</p>
            <div className="space-y-4">
              <ErrorNote error={error} />

              {/* Kontakt-Typ */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Typ</label>
                <div className="flex gap-2">
                  {([['customer', 'Kunde'], ['supplier', 'Lieferant'], ['both', 'Beides']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setEditing({ ...editing, kind: key })}
                      className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                        (editing.kind ?? 'customer') === key
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Firma" value={editing.companyName ?? ''} onChange={(v) => setEditing({ ...editing, companyName: v })} placeholder="z. B. Müller GmbH" hint="Bei Privatkunden leer lassen." error={touched && nameMissing} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Vorname" value={editing.firstName ?? ''} onChange={(v) => setEditing({ ...editing, firstName: v })} placeholder="z. B. Max" error={touched && nameMissing} />
                <Field label="Nachname" value={editing.lastName ?? ''} onChange={(v) => setEditing({ ...editing, lastName: v })} placeholder="z. B. Müller" error={touched && nameMissing} />
              </div>
              {touched && nameMissing && <p className="-mt-2 text-xs text-red-500">Bitte mindestens eine Firma oder einen Namen angeben.</p>}
              <Field label="E-Mail" type="email" inputMode="email" value={editing.email ?? ''} onChange={(v) => setEditing({ ...editing, email: v })} placeholder="z. B. max@example.com" hint="Für den Rechnungsversand per E-Mail." error={touched && emailInvalid} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Telefon" value={editing.phone ?? ''} onChange={(v) => setEditing({ ...editing, phone: v })} placeholder="z. B. 030 123456" />
                <Field label="Ansprechpartner" value={editing.contactPerson ?? ''} onChange={(v) => setEditing({ ...editing, contactPerson: v })} placeholder="z. B. Frau Schmidt" />
              </div>
              <Field label="Straße" value={editing.street ?? ''} onChange={(v) => setEditing({ ...editing, street: v })} placeholder="z. B. Hauptstraße 1" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="PLZ" value={editing.postalCode ?? ''} onChange={(v) => setEditing({ ...editing, postalCode: v })} placeholder="z. B. 10115" />
                <Field label="Ort" value={editing.city ?? ''} onChange={(v) => setEditing({ ...editing, city: v })} placeholder="z. B. Berlin" />
              </div>

              {/* Kategorisierung — nur ab Plus */}
              {canCategorize && (
              <div className="rounded-xl border border-gray-200 p-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Kategorisierung</span>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-amber-700">
                    <input type="checkbox" checked={!!editing.vip} onChange={(e) => setEditing({ ...editing, vip: e.target.checked })} className="h-4 w-4" />
                    <Star className={`h-4 w-4 ${editing.vip ? 'fill-amber-400 text-amber-400' : ''}`} /> VIP-Kunde
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Kategorie" value={editing.category ?? ''} onChange={(v) => setEditing({ ...editing, category: v })} placeholder="z. B. Bestandskunde" />
                  <Field label="Branche" value={editing.industry ?? ''} onChange={(v) => setEditing({ ...editing, industry: v })} placeholder="z. B. Handwerk" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Field label="Region" value={editing.region ?? ''} onChange={(v) => setEditing({ ...editing, region: v })} placeholder="z. B. Berlin" />
                  <Field label="Tags" value={editing.tags ?? ''} onChange={(v) => setEditing({ ...editing, tags: v })} placeholder="z. B. wichtig, online" hint="Komma-getrennt." />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Field label="Standard-Rabatt %" value={editing.discountPercent != null ? String(editing.discountPercent) : ''} onChange={(v) => setEditing({ ...editing, discountPercent: v === '' ? 0 : Number(v.replace(',', '.')) || 0 })} placeholder="0" hint="wird in Rechnungen vorbelegt" />
                </div>
              </div>
              )}

              {/* DSGVO: Einwilligung (Werbung/Kontakt) */}
              {canCategorize && (
                <div className="rounded-xl border border-gray-200 p-3">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Werbe-/Kontakteinwilligung (DSGVO)</label>
                  <select
                    value={editing.consentStatus ?? 'none'}
                    onChange={(e) => setEditing({ ...editing, consentStatus: e.target.value as 'none' | 'given' | 'revoked' })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="none">Keine Angabe</option>
                    <option value="given">Erteilt</option>
                    <option value="revoked">Widerrufen</option>
                  </select>
                  {editing.consentDate && (
                    <p className="mt-1 text-xs text-gray-400">Zuletzt geändert: {new Date(editing.consentDate).toLocaleDateString('de-DE')}</p>
                  )}
                </div>
              )}

              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Vorschau auf der Rechnung</div>
                <div className="text-sm text-gray-800">{displayCustomer(editing) || '—'}</div>
                <div className="text-sm text-gray-500">{[editing.street, [editing.postalCode, editing.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || 'Keine Adresse'}</div>
              </div>
            </div>
            {editing.id && canCategorize && (
              <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
                <button type="button" onClick={() => dsgvoExport(editing.id!)} className="text-xs font-medium text-blue-600 hover:underline">DSGVO-Datenexport</button>
                <span className="text-gray-300">·</span>
                <button type="button" onClick={() => purge(editing.id!)} className="text-xs font-medium text-rose-600 hover:underline">Endgültig löschen</button>
              </div>
            )}
            <div className="mt-6 flex items-center justify-between gap-2">
              <span className="text-xs text-gray-400">{nameMissing ? 'Name oder Firma fehlt' : emailInvalid ? 'E-Mail prüfen' : 'Bereit zum Speichern'}</span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setEditing(null)}>
                  Abbrechen
                </Button>
                <Button onClick={save} busy={busy}>
                  Speichern
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV-Import-Vorschau */}
      {importRows && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6">
            <h2 className="mb-1 text-lg font-bold text-gray-900">CSV-Import — Vorschau</h2>
            <p className="mb-4 text-sm text-gray-500">
              {importRows.length} Zeile{importRows.length === 1 ? '' : 'n'} erkannt. Zeilen ohne Namen/Firma werden übersprungen.
            </p>
            <div className="max-h-[50vh] overflow-auto rounded-lg border border-gray-200">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Name / Firma</th>
                    <th className="px-3 py-2">E-Mail</th>
                    <th className="px-3 py-2">Ort</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {importRows.slice(0, 100).map((r, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1.5">{displayCustomer(r) || <span className="text-rose-500">— wird übersprungen</span>}</td>
                      <td className="px-3 py-1.5 text-gray-600">{r.email ?? ''}</td>
                      <td className="px-3 py-1.5 text-gray-600">{r.city ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {importRows.length > 100 && <div className="px-3 py-2 text-xs text-gray-400">… und {importRows.length - 100} weitere</div>}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setImportRows(null)}>Abbrechen</Button>
              <Button onClick={confirmImport} busy={importing}>{importRows.length} importieren</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
